<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ToggleCTA from "@kontextwerk/web-sdk/ui/actions/ToggleCTA.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiPlus } from "@mdi/js"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { emitEvent } from "../../../../../../../../../_shared/_logic/eventbus"
  import { customerImportLocked } from "../../../../../../../../../_shared/_logic/customer-import-status"
  import {
    CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS,
    customerRegistryActionTooltips,
  } from "../../../../../../_shared/_logic/customer-registry-action-tooltips"
  import CustomerAddressOverlay, {
    type AddressOverlayResult,
  } from "./_modules/customer-address-overlay"
  import type {
    DefaultAddressPrefill,
    FilterDisplayMode,
  } from "../../_shared/_logic/address-table-contract"

  type Props = {
    organizationId: string
    properties?: Property[]
    propertyLinks?: PropertyCustomer[]
    customers?: Customer[]
    canEdit: boolean
    defaultAddress?: DefaultAddressPrefill | null
    importing?: boolean
    triggerRefresh?: () => void | Promise<void>
    onChanged?: (el: { properties: Property[]; links: PropertyCustomer[] }) => void
    title?: string
    filterMode?: FilterDisplayMode | null
    onFilterModeChange?: ((value: FilterDisplayMode) => void) | null
  }

  let {
    organizationId,
    properties = $bindable<Property[]>([]),
    propertyLinks = $bindable<PropertyCustomer[]>([]),
    customers = $bindable<Customer[]>([]),
    canEdit,
    defaultAddress = null,
    importing = $bindable(false),
    triggerRefresh = () => {},
    onChanged,
    title = "Liegenschaften",
    filterMode = null,
    onFilterModeChange = null,
  }: Props = $props()

  const showFilterModeToggle = $derived(Boolean(filterMode && onFilterModeChange))
  const filterModeOptions = [
    { value: "compact", label: "Kompakt" },
    { value: "detailed", label: "Ausführlich" },
  ]

  const safeCustomers = $derived(Array.isArray(customers) ? customers : [])
  const importLocked = $derived(Boolean(importing || $customerImportLocked))

  const handleAddressSaved = ({ property, link, removedLinkId }: AddressOverlayResult) => {
    const propertyId = property.id
    const hasProperty = properties.some((entry) => entry.id === propertyId)
    properties = hasProperty
      ? properties.map((entry) => (entry.id === propertyId ? property : entry))
      : [property, ...properties]

    if (removedLinkId && (!link || removedLinkId !== link.id)) {
      propertyLinks = propertyLinks.filter((entry) => entry.id !== removedLinkId)
    }

    if (link) {
      const linkExists = propertyLinks.some((entry) => entry.id === link.id)
      propertyLinks = linkExists
        ? propertyLinks.map((entry) => (entry.id === link.id ? link : entry))
        : [link, ...propertyLinks]
    }

    onChanged?.({ properties: properties, links: propertyLinks })
    emitEvent("customer-registry.changed", {
      organizationId,
      entity: link ? "property_customer" : "property",
      propertyId,
      customerId: link?.customer_id ?? null,
    })
    triggerRefresh?.()
  }

  const openAddressOverlay = () => {
    if (!canEdit || importLocked) return

    openOverlay({
      title: "Liegenschaft hinzufügen",
      content: CustomerAddressOverlay,
      properties: {
        organizationId,
        customers: safeCustomers,
        defaultAddress,
        property: null,
        onSaved: handleAddressSaved,
      },
    })
  }
</script>

{#snippet headerActions()}
  {#if showFilterModeToggle}
    <ToggleCTA
      class="address-filter-mode-toggle"
      dataTestid="address-table.filterModeToggle"
      options={filterModeOptions}
      value={filterMode ?? "compact"}
      darkMode={false}
      onChange={(value) => {
        const nextValue = `${value ?? filterMode ?? "compact"}`.toLowerCase()
        onFilterModeChange?.(nextValue === "detailed" ? "detailed" : "compact")
      }}
    />
  {/if}
    <div class="address-actions">
      <CTA
      type="secondaryButton"
      content=""
      tooltipContent={importLocked
        ? customerRegistryActionTooltips.importLocked
        : customerRegistryActionTooltips.propertyAdd}
      tooltipClassName={CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS}
      onClick={openAddressOverlay}
      disabled={!canEdit || importLocked}
    >
        {#snippet iconSnippet()}
          <Icon path={mdiPlus} />
        {/snippet}
      </CTA>
    </div>
{/snippet}

  <SectionTitle
  brightBackground={true}
  {title}
  styleOuter="margin-bottom: 1.2rem;"
  helpModalTextId="customerRegistry"
  buttonSnippet={headerActions}
/>
<style lang="less">
  .address-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  :global(.address-filter-mode-toggle) {
    flex: 0 0 auto;
    min-width: max-content;
  }

  :global(.address-filter-mode-toggle button.cta.toggle) {
    white-space: nowrap;
  }
</style>
